import React from 'react';
import { GetServerSideProps } from 'next';
import * as BREAKPOINTS from '@zola/zola-ui/src/lib/ui/breakpoints';
import type { WRelatedLinkView } from '@zola/svc-web-api-ts-client';

// props
import { fetchUser } from 'libs/server/users';
import { UserContext } from '@zola-helpers/client/dist/es/@types';
import { fetchNavHTML } from 'libs/server/nav';

// Context
import NAV_DATA from '@zola-helpers/client/dist/es/constants/navConstants';

import { ServerResponse } from 'http';
import IncomingMessageWithCookies from 'types/IncomingMessageWithCookies';

// Page Sections
import Header from 'components/LP/GuestList/components/Header';
import Nav from 'components/Nav/Nav';
import Masthead from '@zola/zola-ui/src/components/PreAuthV3/Masthead';
import Differentiation from '@zola/zola-ui/src/components/PreAuthV3/Differentiation';
import SupportAccordion from '@zola/zola-ui/src/components/PreAuthV3/SupportAccordion';
import { PreauthFooterV3 } from '@zola/zola-ui/src/components/PreAuthV3';
import { BackgroundType } from '@zola/zola-ui/src/components/PreAuthV3/FeaturedImage';

// Hooks
import { useIsPostAuthNav3MobileOrDesktop } from 'hooks/useIsPostAuthNav3Active';

import { fetchSEORelatedLinks } from 'libs/server/seo';

// Content
import { differentiationContent, FAQItems, reviewItems } from 'components/LP/GuestList/content';

// Hero Images
import HeroBackgroundMobile from '../../../../public/static-preauth/images/GuestListLP/Masthead/background_mobile.jpg';
import HeroBackgroundMobileWebp from '../../../../public/static-preauth/images/GuestListLP/Masthead/background_mobile.webp';
import HeroBackgroundDesktop from '../../../../public/static-preauth/images/GuestListLP/Masthead/background_desktop.jpg';
import HeroBackgroundDesktopWebp from '../../../../public/static-preauth/images/GuestListLP/Masthead/background_desktop.webp';
import HeroForegroundMobile from '../../../../public/static-preauth/images/GuestListLP/Masthead/foreground_mobile.png';
import HeroForegroundTablet from '../../../../public/static-preauth/images/GuestListLP/Masthead/foreground_tablet.png';
import HeroForegroundDesktop from '../../../../public/static-preauth/images/GuestListLP/Masthead/foreground_desktop.png';

import { PageWrapper } from '../../../components/LP/v3/PageWrapper.styles';

export type GuestListLPProps = {
  navHTML: string | null;
  userCtx: UserContext | null;
  adviceLinks: WRelatedLinkView[];
};

/** ******************************************************************************************
 *                                                                                           *
 *            This page should adhere to the web-preauth LP style guide:                     *
 *                                                                                           *
 * https://github.com/NewAmsterdamLabs/web-preauth/wiki/Pre-auth-Landing-Page-Style-Guide    *
 *                                                                                           *
 ****************************************************************************************** */

const GuestList: React.FC<GuestListLPProps> = ({ navHTML, userCtx, adviceLinks }) => {
  const isPostAuthNav3Enabled = useIsPostAuthNav3MobileOrDesktop(userCtx);

  return (
    <>
      <Header />
      <Nav
        activePrimaryLink={NAV_DATA.PRIMARY.GUEST_LIST}
        navHTML={navHTML}
        user={userCtx}
        showPromo
        disablePrimaryNavCollapse
        disableSecondary
      />
      <PageWrapper isPostAuthNav3={isPostAuthNav3Enabled}>
        <Masthead
          btnHref="/wedding/onboard/wedding-guest-list"
          btnText="Start your guest list"
          isPhotoOnRight
          images={{
            background: {
              parameters: {
                alt: '',
                featuredSrc: HeroBackgroundDesktop,
                jpg: [
                  { url: HeroBackgroundMobile, breakpoint: BREAKPOINTS.SCREEN_XS.toString() },
                  { url: HeroBackgroundDesktop, breakpoint: BREAKPOINTS.SCREEN_SM_MAX.toString() },
                  { url: HeroBackgroundDesktop, breakpoint: BREAKPOINTS.SCREEN_MD_MAX.toString() },
                ],
                webp: [
                  { url: HeroBackgroundMobileWebp, breakpoint: BREAKPOINTS.SCREEN_XS.toString() },
                  {
                    url: HeroBackgroundDesktopWebp,
                    breakpoint: BREAKPOINTS.SCREEN_SM_MAX.toString(),
                  },
                  {
                    url: HeroBackgroundDesktopWebp,
                    breakpoint: BREAKPOINTS.SCREEN_MD_MAX.toString(),
                  },
                ],
              },
              type: BackgroundType.IMAGE,
            },
            foregroundImage: {
              desktopSrc: HeroForegroundDesktop,
              imgType: 'png',
              mobileSrc: HeroForegroundTablet,
              tabletSrc: HeroForegroundMobile,
            },
          }}
          title={
            <h1>
              Build a <span>guest list</span> that does the work for you
            </h1>
          }
          valueProps={[
            'Grab guest info in one go with Contact Collector',
            'We’ll track everything for you — RSVPs, plus ones, and meal choices',
            'Simplify the way you send invites, reminders, or updates',
          ]}
        />
        <Differentiation
          content={differentiationContent}
          title={{
            titleStart: 'We’ve',
            highlightText: 'simplified',
            titleEnd: 'every part of managing your guest list',
          }}
          isPhotoOnRight={false}
        />
        <div>
          <SupportAccordion
            advice={{ links: adviceLinks }}
            faqs={FAQItems}
            reviews={reviewItems}
            openSections={['faqs']}
          />
        </div>
        <PreauthFooterV3 isGuest={userCtx ? Boolean(userCtx.is_guest) : true} />
      </PageWrapper>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<GuestListLPProps> = async ({
  req,
}: {
  req: IncomingMessageWithCookies;
  res: ServerResponse;
}) => {
  const [navHTML, userCtx, adviceLinks] = await Promise.all([
    fetchNavHTML(req),
    fetchUser(req),
    fetchSEORelatedLinks({
      topicSlug: 'wedding-planning-101',
      subtopicSlug: 'wedding-websites',
    }),
  ]);

  return {
    props: {
      navHTML,
      userCtx,
      adviceLinks,
    },
  };
};

export default GuestList;
